import type {
  BlockCondition,
  CanvasBlock,
  FmSentFormRow,
  FormListItem,
  FormPackage,
  FormPackageFormOption,
  FormsKpiStats,
} from '@elva/ui';
import type { BuilderBlockSource } from '@elva/ui';
import {
  CONDITION_OPERATOR_FROM_WIRE,
  CONDITION_OPERATOR_TO_WIRE,
  invertWireOperator,
  isConditionAction,
} from '@elva/ui';
import type {
  AssignmentStatus,
  FormAssignmentsResponse,
  FormBlock,
  FormBlockCatalogEntry,
  FormBlockCatalogResponse,
  FormBlockWrite,
  FormCondition,
  FormConditionRule,
  FormConditionWrite,
  FormListRecord,
  FormPackageRecord,
  FormPackagesResponse,
  FormStatsResponse,
  FormTemplateDetail,
  FormTemplateStatus,
  RawFormBlockCatalogEntry,
  SyncStatus,
} from '../api/types';
import { formsApi } from '../api/formsApi';
import {
  buildCatalogIndex,
  buildWireBlockConfig,
  lookupCatalogEntry,
  readSmartBlockOverrides,
  readUiBlockConfig,
  readUiSmartBlockExtras,
} from './formsBlockConfig';
import { isAxiosError } from 'axios';
import { invalidateFormTemplateEtagCache } from '@/api/etagCache';

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

const ASSIGNMENT_STATUS_MAP: Record<AssignmentStatus, FmSentFormRow['assignmentStatus']> = {
  pending: 'pending',
  in_progress: 'in_progress',
  submitted: 'submitted',
  incomplete: 'incomplete',
  approved: 'approved',
  rejected: 'incomplete',
  cancelled: 'cancelled',
  expired: 'expired',
};

/** The UI only distinguishes synced vs. not-yet-synced; syncing/failed collapse to not_started. */
function toRowSyncStatus(status: SyncStatus | null | undefined): FmSentFormRow['syncStatus'] {
  return status === 'synced' ? 'synced' : 'not_started';
}

/** Sync status per assignment/submission id — for joining onto the per-patient forms tab. */
export function adaptAssignmentSyncStatusMap(
  response: FormAssignmentsResponse | undefined,
): Record<string, FmSentFormRow['syncStatus']> {
  const map: Record<string, FmSentFormRow['syncStatus']> = {};
  for (const item of response?.results ?? []) {
    const status = toRowSyncStatus(item.sync_status);
    if (item.assignment_id) map[item.assignment_id] = status;
    if (item.submission_id) map[item.submission_id] = status;
  }
  return map;
}

/** GET /api/forms/assignments → the location-scoped Sent forms review queue, with the real ledger. */
export function adaptFormAssignments(response: FormAssignmentsResponse | undefined): FmSentFormRow[] {
  return (response?.results ?? []).map((item) => ({
    id: item.assignment_id,
    patientId: String(item.patient_id),
    patientName: item.patient_name,
    formName: item.form_name,
    formDesc: '',
    assignmentStatus: ASSIGNMENT_STATUS_MAP[item.assignment_status] ?? 'pending',
    syncStatus: toRowSyncStatus(item.sync_status),
    timeline: {
      ...(item.sent_at ? { sentAt: formatWhen(item.sent_at) } : {}),
      ...(item.approved_at ? { closedAt: formatWhen(item.approved_at) } : {}),
    },
    ...(item.submission_id ? { submissionId: item.submission_id } : {}),
  }));
}

function formatTrigger(trigger: string | undefined, auto: boolean): string {
  const trimmed = trigger?.trim() ?? '';
  if (trimmed) return trimmed;
  if (auto) return 'Auto-send on booking';
  return 'Not configured';
}

function formatLastSent(sent: number, updatedAt: string | undefined): string {
  if (sent <= 0) return 'never';
  return updatedAt ? formatWhen(updatedAt) : 'never';
}

function mapTemplateStatus(status: FormListRecord['status']): FormListItem['status'] {
  if (status === 'active' || status === 'published') return 'published';
  if (status === 'archived') return 'archived';
  return 'draft';
}

export function normalizeFormListRecords(data: {
  results?: FormListRecord[];
  forms?: FormListRecord[];
}): FormListRecord[] {
  return data.results ?? data.forms ?? [];
}

export function adaptFormTemplate(t: FormListRecord): FormListItem {
  return {
    id: t.id,
    name: t.name,
    desc: t.desc ?? t.description ?? '',
    status: mapTemplateStatus(t.status),
    trigger: formatTrigger(t.trigger, t.auto ?? false),
    submitted: t.submitted ?? 0,
    sent: t.sent ?? 0,
    completion: t.completion ?? 0,
    blocks: t.blocks ?? t.block_count ?? 0,
    compliance: t.compliance ?? false,
    auto: t.auto ?? false,
    lastSent: formatLastSent(t.sent ?? 0, t.updated_at),
  };
}

export function adaptFormStats(
  stats: FormStatsResponse | undefined,
  queuePendingCount = 0,
): FormsKpiStats {
  return {
    sent: stats?.sent ?? 0,
    completed: stats?.completed ?? 0,
    completionRate: stats?.completion_rate ?? 0,
    pendingReview: stats?.pending_review ?? queuePendingCount,
  };
}

export function adaptFormPackage(record: FormPackageRecord): FormPackage {
  return {
    id: record.id,
    name: record.name,
    description: record.description ?? '',
    trigger: record.trigger ?? '',
    forms: record.forms ?? [],
    ...(record.locked != null ? { locked: record.locked } : {}),
  };
}

export function adaptFormPackageOptions(forms: FormListItem[]): FormPackageFormOption[] {
  return forms.map((form) => ({
    id: form.id,
    name: form.name,
    ...(form.desc ? { desc: form.desc } : {}),
  }));
}

export function normalizeFormPackagesResponse(
  data: FormPackagesResponse | FormPackageRecord[] | undefined | null,
): FormPackageRecord[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.packages ?? data.results ?? [];
}

/**
 * W14/D41: normalize the served catalog into a flat entry list keyed by `type`.
 *
 * The real backend (`serialize_catalog()`) nests blocks under
 * `blocks.{basic,patient_info,smart_block}` and names the id `block_type` — the
 * previous reader looked for top-level `basic`/`patient_info`/`smart_blocks` with
 * a `type` field, so it returned `[]` at runtime (the root of the author/save
 * drift). Read the real shape, alias `block_type → type`, keep server `block_kind`.
 */
export function flattenBlockCatalog(response: FormBlockCatalogResponse): FormBlockCatalogEntry[] {
  if (response.catalog?.length) return response.catalog;

  const blocks = response.blocks;
  if (blocks) {
    const nested: [string, RawFormBlockCatalogEntry[] | undefined][] = [
      ['basic', blocks.basic],
      ['patient_info', blocks.patient_info],
      ['smart_block', blocks.smart_block],
    ];
    return nested.flatMap(([kind, items]) =>
      (items ?? []).map((item): FormBlockCatalogEntry => ({
        ...item,
        type: item.block_type,
        block_kind: item.block_kind ?? kind,
        category: item.category ?? item.block_kind ?? kind,
      })),
    );
  }

  const grouped: [string, FormBlockCatalogEntry[] | undefined][] = [
    ['basic', response.basic],
    ['patient_info', response.patient_info],
    ['smart_blocks', response.smart_blocks],
  ];

  return grouped.flatMap(([category, items]) =>
    (items ?? []).map((item) => ({
      ...item,
      category: item.category ?? item.block_kind ?? category,
    })),
  );
}

function resolveBlockSource(
  blockKind?: string,
  category?: string,
  blockType?: string,
): CanvasBlock['source'] {
  const normalized = (blockKind ?? category ?? '').trim().toLowerCase();
  if (
    normalized === 'smart' ||
    normalized === 'smart_blocks' ||
    normalized === 'smart_block'
  ) {
    return 'smart';
  }
  if (normalized === 'patient' || normalized === 'patient_info') return 'patient';

  const type = (blockType ?? '').trim().toLowerCase();
  if (type.startsWith('sb_')) return 'smart';
  if (type.startsWith('patient_')) return 'patient';

  return 'basic';
}

function normalizeSmartBlockTypeId(typeId: string): string {
  if (!typeId || typeId.startsWith('sb_')) return typeId;
  return `sb_${typeId}`;
}

function readBlockPosition(block: FormBlock): number {
  return block.position ?? block.order ?? 0;
}

/** The server echoes rules as `block_id` / `operator` or `field` / `op`; read either. */
function readConditionRule(
  raw: FormConditionRule | undefined,
): Pick<FormConditionRule, 'field' | 'op' | 'value' | 'action'> | undefined {
  if (!raw) return undefined;
  const field = raw.field || raw.block_id;
  const op = raw.op || raw.operator;
  if (!field || !op) return undefined;
  return { field, op, value: raw.value ?? null, ...(raw.action ? { action: raw.action } : {}) };
}

function adaptConditionToUi(
  condition: FormCondition,
  blocks: FormBlock[],
  blockLabels: Map<string, string>,
): BlockCondition | undefined {
  const rule = readConditionRule(condition.rules[0]);
  if (!rule) return undefined;

  const sourceBlock = blocks.find((b) => b.id === rule.field);
  const fieldLabel = blockLabels.get(rule.field) ?? sourceBlock?.config?.label ?? rule.field;
  const action = isConditionAction(rule.action) ? rule.action : 'show';
  const wireOp = action === 'hide' ? (invertWireOperator(rule.op) ?? rule.op) : rule.op;

  return {
    fieldUid: rule.field,
    fieldLabel,
    operator: CONDITION_OPERATOR_FROM_WIRE[wireOp] ?? 'is equal to',
    value: rule.value != null ? String(rule.value) : '',
    mode: condition.rules.length > 1 ? 'advanced' : 'simple',
    action,
  };
}

/**
 * The server evaluates a condition as "block visible when it matches". Hide is
 * stored as show-with-inverted-operator; `action` rides along in the rule JSON
 * (the server keeps it verbatim) so the builder can round-trip the author's intent.
 */
export function serializeConditionToApi(
  condition: BlockCondition,
  sourceServerId: string,
): FormConditionWrite {
  const action = condition.action ?? 'show';
  const baseOp = CONDITION_OPERATOR_TO_WIRE[condition.operator];
  const op = action === 'hide' ? (invertWireOperator(baseOp) ?? baseOp) : baseOp;
  const valueless = baseOp === 'empty' || baseOp === 'not_empty';

  return {
    operator: 'AND',
    rules: [
      {
        field: sourceServerId,
        op,
        value: valueless ? null : condition.value,
        action,
      },
    ],
  };
}

export function adaptFormBlocks(
  blocks: FormBlock[],
  conditions: FormCondition[] = [],
): CanvasBlock[] {
  const conditionById = new Map(conditions.map((c) => [c.id, c]));
  const blockLabels = new Map(
    blocks.map((b) => [b.id, b.config?.label ?? b.label ?? b.block_type ?? b.type ?? '']),
  );

  return [...blocks]
    .sort((a, b) => readBlockPosition(a) - readBlockPosition(b))
    .map((block): CanvasBlock => {
      const rawTypeId = block.block_type ?? block.config?.block_type ?? block.type ?? '';
      const source = resolveBlockSource(block.block_kind, block.category, rawTypeId);
      const typeId =
        source === 'smart' ? normalizeSmartBlockTypeId(rawTypeId) : rawTypeId;
      const label = block.config?.label ?? block.label ?? typeId;
      const linkedCondition = block.condition_id
        ? conditionById.get(block.condition_id)
        : undefined;
      const condition = linkedCondition
        ? adaptConditionToUi(linkedCondition, blocks, blockLabels)
        : undefined;

      const base: CanvasBlock = {
        uid: block.id,
        source,
        typeId,
        title: label,
      };

      if (source === 'smart') {
        const overrides = readSmartBlockOverrides(block);
        return {
          ...base,
          ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
          ...readUiSmartBlockExtras(block),
          ...(condition ? { condition } : {}),
        };
      }

      const uiConfig = readUiBlockConfig(block, source);
      return {
        ...base,
        ...(uiConfig ? { config: uiConfig } : {}),
        ...(condition ? { condition } : {}),
      };
    });
}

function resolveBlockKind(block: CanvasBlock, catalogEntry?: FormBlockCatalogEntry): string {
  if (catalogEntry?.block_kind) return catalogEntry.block_kind;
  const category = catalogEntry?.category?.trim().toLowerCase();
  if (category === 'patient_info' || category === 'patient') return 'patient_info';
  if (category === 'smart_blocks' || category === 'smart') return 'smart_block';
  if (block.source === 'patient') return 'patient_info';
  if (block.source === 'smart') return 'smart_block';
  return 'basic';
}

export function serializeCanvasBlock(
  block: CanvasBlock,
  position: number,
  catalogIndex: Map<string, FormBlockCatalogEntry>,
  conditionId?: string | null,
): FormBlockWrite {
  const catalogEntry = lookupCatalogEntry(catalogIndex, block);
  const blockType = catalogEntry?.type ?? block.typeId;
  const config = buildWireBlockConfig(block, catalogEntry);

  return {
    block_kind: resolveBlockKind(block, catalogEntry),
    block_type: blockType,
    config,
    is_required:
      (block.config?.required ?? block.required ?? false) || block.condition?.action === 'require',
    // A conditioned block starts hidden and appears when its (show-form) rule matches.
    is_visible_default: !block.condition?.fieldUid,
    position,
    condition_id: conditionId ?? null,
  };
}

function isSameConditionWrite(write: FormConditionWrite, stored: FormCondition): boolean {
  if (write.operator !== stored.operator || write.rules.length !== stored.rules.length) return false;
  return write.rules.every((rule, i) => {
    const prev = readConditionRule(stored.rules[i]);
    return (
      prev !== undefined &&
      prev.field === rule.field &&
      prev.op === rule.op &&
      (prev.value ?? null) === (rule.value ?? null) &&
      (prev.action ?? 'show') === (rule.action ?? 'show')
    );
  });
}

/**
 * Runs after blocks are upserted, so a rule's source can be resolved to its
 * server id — a canvas uid for a block created in this same save is local-only.
 * Orphaned conditions are deleted by the caller once blocks are relinked.
 */
async function syncConditions(
  formId: string,
  canvasBlocks: CanvasBlock[],
  serverIdByUid: Map<string, string>,
  priorConditionIdByServerId: Map<string, string | null>,
  existingConditions: FormCondition[],
): Promise<Map<string, string | null>> {
  const conditionIdByUid = new Map<string, string | null>();
  const existingIds = new Set(existingConditions.map((c) => c.id));

  for (const block of canvasBlocks) {
    const sourceUid = block.condition?.fieldUid;
    const sourceServerId = sourceUid ? (serverIdByUid.get(sourceUid) ?? sourceUid) : undefined;
    const targetServerId = serverIdByUid.get(block.uid);
    if (!block.condition || !sourceServerId || !targetServerId) {
      conditionIdByUid.set(block.uid, null);
      continue;
    }

    const payload = serializeConditionToApi(block.condition, sourceServerId);
    const priorId = priorConditionIdByServerId.get(targetServerId) ?? null;

    if (priorId && existingIds.has(priorId)) {
      const stored = existingConditions.find((c) => c.id === priorId);
      if (!stored || !isSameConditionWrite(payload, stored)) {
        await formsApi.patchCondition(formId, priorId, payload);
      }
      conditionIdByUid.set(block.uid, priorId);
    } else {
      const created = await formsApi.createCondition(formId, payload);
      conditionIdByUid.set(block.uid, created.id);
    }
  }

  return conditionIdByUid;
}

/**
 * Config keys the builder UI cannot edit (no CanvasBlock source) but the server
 * may have set. PATCH replaces `config`, so carry them over to avoid wiping them.
 */
const SERVER_PRESERVED_CONFIG_KEYS = ['prefill', 'ai_prompt_mode'] as const;

function preserveServerConfigKeys(
  payload: FormBlockWrite,
  serverBlock: FormBlock | undefined,
): FormBlockWrite {
  if (!serverBlock?.config) return payload;
  const carried: Record<string, unknown> = {};
  for (const key of SERVER_PRESERVED_CONFIG_KEYS) {
    const value = serverBlock.config[key];
    if (value !== undefined && payload.config?.[key] === undefined) {
      carried[key] = value;
    }
  }
  if (Object.keys(carried).length === 0) return payload;
  return { ...payload, config: { ...(payload.config ?? {}), ...carried } };
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/** Only blocks the author actually changed are PATCHed — an untouched block is left as stored. */
function isSameBlockWrite(write: FormBlockWrite, stored: FormBlock): boolean {
  return (
    write.block_kind === stored.block_kind &&
    write.block_type === stored.block_type &&
    write.position === stored.position &&
    Boolean(write.is_required) === Boolean(stored.is_required) &&
    (write.is_visible_default ?? true) === (stored.is_visible_default ?? true) &&
    (write.condition_id ?? null) === (stored.condition_id ?? null) &&
    stableJson(write.config ?? {}) === stableJson(stored.config ?? {})
  );
}

function readCreatedBlockId(created: FormBlock): string {
  const id = created.id?.trim();
  if (id) return id;
  throw new Error('Block create response missing id');
}

async function reorderFormBlocksByCanvasOrder(
  formId: string,
  canvasBlocks: CanvasBlock[],
  uidMap: Map<string, string>,
  initialServerIds: Set<string>,
): Promise<void> {
  const orderedIds = canvasBlocks
    .map((block) => uidMap.get(block.uid) ?? (initialServerIds.has(block.uid) ? block.uid : null))
    .filter((id): id is string => id != null && id !== '');

  if (orderedIds.length <= 1) return;

  await formsApi.reorderBlocks(formId, { ordered_block_ids: orderedIds });
}

export async function syncFormBlocks(
  formId: string,
  canvasBlocks: CanvasBlock[],
  existingConditions: FormCondition[] = [],
): Promise<CanvasBlock[]> {
  invalidateFormTemplateEtagCache(formId);

  const [{ blocks: serverBlocks = [] }, catalogResponse, conditionsResponse] = await Promise.all([
    formsApi.getBlocks(formId),
    formsApi.getBlockCatalog(),
    formsApi.getConditions(formId).catch(() => ({ conditions: existingConditions })),
  ]);

  const catalog = flattenBlockCatalog(catalogResponse);
  const catalogIndex = buildCatalogIndex(catalog);
  const conditions = conditionsResponse.conditions ?? existingConditions;
  const serverIds = new Set(serverBlocks.map((block) => block.id));
  const priorConditionIdByServerId = new Map(serverBlocks.map((b) => [b.id, b.condition_id ?? null]));

  const uidMap = new Map<string, string>();
  const keptServerIds = new Set<string>();
  const writtenPayloads = new Map<string, FormBlockWrite>();

  for (let index = 0; index < canvasBlocks.length; index += 1) {
    const block = canvasBlocks[index];
    if (!block) continue;

    let existingServerId =
      uidMap.get(block.uid) ?? (serverIds.has(block.uid) ? block.uid : undefined);

    const draft = serializeCanvasBlock(block, index, catalogIndex, null);
    if (!existingServerId) {
      // Positional reuse only for the same block type — the server validates a
      // PATCH's config against the stored type, so a heading can't become a paragraph.
      const positional = serverBlocks[index];
      if (positional?.id && !keptServerIds.has(positional.id) && positional.block_type === draft.block_type) {
        existingServerId = positional.id;
      }
    }

    const priorConditionId = existingServerId
      ? (priorConditionIdByServerId.get(existingServerId) ?? null)
      : null;
    const payload = { ...draft, condition_id: priorConditionId };

    if (existingServerId) {
      keptServerIds.add(existingServerId);
      uidMap.set(block.uid, existingServerId);
      const serverBlock = serverBlocks.find((b) => b.id === existingServerId);
      const full = preserveServerConfigKeys(payload, serverBlock);
      if (!serverBlock || !isSameBlockWrite(full, serverBlock)) {
        await formsApi.patchBlock(formId, existingServerId, full);
      }
      writtenPayloads.set(existingServerId, full);
      continue;
    }

    const created = await formsApi.createBlock(formId, payload);
    const newId = readCreatedBlockId(created);
    keptServerIds.add(newId);
    uidMap.set(block.uid, newId);
    writtenPayloads.set(newId, payload);
  }

  for (const serverBlock of serverBlocks) {
    if (!keptServerIds.has(serverBlock.id)) {
      await formsApi.deleteBlock(formId, serverBlock.id);
    }
  }

  const conditionIds = await syncConditions(
    formId,
    canvasBlocks,
    uidMap,
    priorConditionIdByServerId,
    conditions,
  );

  for (const block of canvasBlocks) {
    const serverId = uidMap.get(block.uid);
    if (!serverId) continue;
    const written = writtenPayloads.get(serverId);
    const nextConditionId = conditionIds.get(block.uid) ?? null;
    if (!written || (written.condition_id ?? null) === nextConditionId) continue;
    await formsApi.patchBlock(formId, serverId, { ...written, condition_id: nextConditionId });
  }

  const inUse = new Set([...conditionIds.values()].filter((id): id is string => Boolean(id)));
  for (const condition of conditions) {
    if (!inUse.has(condition.id)) {
      await formsApi.deleteCondition(formId, condition.id);
    }
  }

  const storedOrder = [...serverBlocks]
    .sort((a, b) => readBlockPosition(a) - readBlockPosition(b))
    .map((b) => b.id);
  const nextOrder = canvasBlocks.map((b) => uidMap.get(b.uid));
  const orderChanged =
    storedOrder.length !== nextOrder.length || storedOrder.some((id, i) => id !== nextOrder[i]);

  try {
    if (orderChanged) await reorderFormBlocksByCanvasOrder(formId, canvasBlocks, uidMap, serverIds);
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 400) throw error;
  }

  const { blocks: refreshed = [] } = await formsApi.getBlocks(formId);
  const freshConditions =
    (await formsApi.getConditions(formId).catch(() => ({ conditions }))).conditions ?? conditions;
  return adaptFormBlocks(refreshed, freshConditions);
}

export function buildFormTemplatePayload(
  name: string,
  status: FormTemplateStatus = 'draft',
  description?: string,
): Partial<FormTemplateDetail> {
  return {
    name,
    status,
    ...(description ? { description } : {}),
  };
}

export async function saveFormTemplate(
  formId: string,
  payload: Partial<FormTemplateDetail>,
  blocks: CanvasBlock[],
  conditions: FormCondition[] = [],
): Promise<{ formId: string; blocks: CanvasBlock[] }> {
  // Always the same template id, even once it has approved submissions: `is_locked` on the
  // template detail is informational only, and forking is opt-in versioning, not an edit path.
  await formsApi.updateTemplate(formId, payload);
  const synced = await syncFormBlocks(formId, blocks, conditions);
  return { formId, blocks: synced };
}
